import type { Swiper as SwiperType } from "swiper";
import type { HomeIntroSlideData, HomeServiceItemData, HomeTrustedBrandItemData, IHandleGetFeaturedProductsParams } from "@model/Home.model";

import homeHero from "Utils/Assets/Images/home.webp";
import heroOfficeAutomation from "Utils/Assets/Images/office.webp";
import heroSystem from "Utils/Assets/Images/system-integration.webp";
import { IProductCardProps, ProductsResponse } from "@model/Products.model";
import { AxiosCalls } from "./Functions/AxiosCalls";
import { handleDefaultApiErrors } from "Utils/Global/apiErrorHandling";

export class HomeService {

    getHomeIntroSlides(): HomeIntroSlideData[] {
        return [
            {
                id: "home-1",
                imageUrl: homeHero,
                eyebrowLeft: "CHIMERATECH",
                eyebrowRight: "Smart living, made personal",
                title: "Your home, beautifully in control.",
                subtitle:
                    "Create the perfect mood in every moment. Lights, curtains, climate, and daily routines working together with effortlesscontrol.",
                bottomTag: "Smart living, made personal",
            },
            {
                id: "home-2",
                imageUrl: heroOfficeAutomation,
                eyebrowLeft: "CHIMERATECH",
                eyebrowRight: "Comfort that feels natural",
                title: "A home that moves with your life.",
                subtitle:
                    "From quiet mornings to relaxed evenings, Chimera brings comfort, privacy, and control into one seamless experiencedesigned around the way you live.",
                bottomTag: "Comfort that feels natural",
            },
            {
                id: "home-3",
                imageUrl: heroSystem,
                eyebrowLeft: "CHIMERATECH",
                eyebrowRight: "Effortless automation",
                title: "Technology that feels invisible.",
                subtitle:
                    "The best smart home does not feel complicated. It simply responds, supports your routine, and makes every day feelsmoother.",
                bottomTag: "Effortless automation",
            },
        ];
    }

    navigateToSlideIndex(
        swiper: SwiperType | null,
        index: number,
        setActiveIndex: (idx: number) => void
    ): void {
        if (!swiper) return;
        swiper.slideToLoop(index);
        setActiveIndex(index);
    }
    getHomeServices(): HomeServiceItemData[] {
        return [
            {
                id: "home-automation",
                iconKind: "home",
                categoryLabel: "Smart living",
                title: "Home Automation",
                description:
                    "Turn everyday actions into effortless moments. Chimera helps your home prepare the right lighting, comfort, privacy, andatmosphere whether you are starting the day, coming back home, or winding down at night.",
                ctaLabel: "Learn more",
                ctaHref: "#home-automation",
            },
            {
              id: "smart-control",
              iconKind: "power",
              categoryLabel: "Personal comfort",
              title: "Smart Control Experiences",
              description:
              "Your space should feel like it understands you. With Chimera, control becomes simple, natural, and always within reach,giving you the freedom to shape every room around your mood and routine.",
              ctaLabel: "Learn more",
              ctaHref: "#smart-control",
            },
            {
                id: "software-dev",
                iconKind: "code",
                categoryLabel: "Digital experiences",
                title: "Custom Software Development",
                description:
                    "Beyond automation, Chimera builds custom apps, dashboards, portals, and digital platforms designed around realneeds. Clean, practical software that helps people interact with technology more easily.",
                ctaLabel: "Learn more",
                ctaHref: "#software-development",
            },
        ];
    }


 async handleGetFeaturedProducts({
    limit,
    setLoading,
    setApiError,
    setItems,
  }: IHandleGetFeaturedProductsParams): Promise<void> {
    setLoading(true);
    setApiError(null);

    try {
      const query = new URLSearchParams();

      if (
        typeof limit === "number" &&
        Number.isFinite(limit) &&
        limit > 0
      ) {
        query.set("limit", String(limit));
      }

      const resp = await AxiosCalls.Get<ProductsResponse>(
        `/category/products/featured?${query.toString()}`
      );

      const data = resp?.data ?? {};
      const rawItems = Array.isArray(data.items) ? data.items : [];

      const normalized: IProductCardProps[] = rawItems.map((p: any) => {
        const priceNum = Number(p?.price ?? 0);

        const stockNum = Number.isFinite(Number(p?.stock))
          ? Number(p.stock)
          : 0;

        const maxPerUserRaw = Number(p?.maxPerUser);
        const maxPerUser =
          Number.isFinite(maxPerUserRaw) && maxPerUserRaw > 0
            ? maxPerUserRaw
            : undefined;

        const firstImageUrl =
          Array.isArray(p?.images) && p.images.length
            ? p.images[0]?.url ?? ""
            : "";

        const product: IProductCardProps = {
          id: String(p.id),
          name: String(p.name ?? ""),
          slug: String(p.slug ?? ""),
          price: priceNum,
          shortDescription: String(p.shortDescription ?? ""),
          imageUrl: firstImageUrl,
          stock: stockNum,
          maxPerUser,
        };

        return product;
      });

      setItems(normalized);
    } catch (err) {
      setApiError(handleDefaultApiErrors(err));
      setItems([]);
    }finally {
      setLoading(false);
    }
  }

    getTrustedBrands(): HomeTrustedBrandItemData[] {
        return [
            {
                id: "wago",
                name: "WAGO",
                logoUrl: "https://be.chimeratech.net/uploads/Wago-logo.webp",
            },
            {
                id: "schneider",
                name: "Schneider Electric",
                logoUrl: "https://be.chimeratech.net/uploads/Schneider-logo.webp",
            },
            {
                id: "mdt",
                name: "MDT",
                logoUrl: "https://be.chimeratech.net/uploads/MDT-logo.webp",
            },
            {
                id: "knx",
                name: "KNX",
                logoUrl: "https://be.chimeratech.net/uploads/KNX-logo.webp",
            },
        ];
    }

}
